import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import { usePlatformVoiceWritingOptions } from '../api/hooks';

const providers = ['groq', 'infercom', 'deepinfra', 'openrouter'];
const labels: Record<string, string> = {
  groq: 'Groq',
  infercom: 'Infercom',
  deepinfra: 'DeepInfra',
  openrouter: 'OpenRouter',
};
const sources: Record<string, string> = {
  environment: 'Deployment key',
  secret_file: 'Deployment secret file',
  cortex_global: 'Cortex global key',
  cortex_tenant: 'Tenant key',
  development: 'Development key',
};

export function SpeechCredentials() {
  const api = useApiClient();
  const rudi = usePlatformVoiceWritingOptions();
  const chat = useQuery({
    queryKey: ['chat-speech-models'],
    queryFn: async () =>
      (
        await api.get<{ models: { provider: string; configured: boolean }[] }>(
          '/chat-model/speech/models'
        )
      ).data,
    retry: false,
  });
  return (
    <section
      className="my-6 space-y-3 rounded-3xl border p-5"
      aria-label="Speech provider credentials"
    >
      <h2 className="text-lg font-semibold">Speech provider credentials</h2>
      <p className="text-sm text-muted-foreground">
        OpenBao supplies deployment keys separately for each application and environment. A key
        stored elsewhere in OpenBao must first be provisioned for that application. Values are never
        shown here.
      </p>
      <table className="w-full text-left text-sm">
        <thead>
          <tr>
            <th scope="col">Provider</th>
            <th scope="col">Rudi</th>
            <th scope="col">Patient Chat</th>
          </tr>
        </thead>
        <tbody>
          {providers.map(provider => {
            const credential = rudi.data?.credentials?.find(
              item => item.credential_ref === `platform/${provider}`
            );
            const configured = chat.data?.models.some(
              item => item.provider === provider && item.configured
            );
            return (
              <tr key={provider} className="border-t">
                <th scope="row" className="py-2 font-medium">
                  {labels[provider]}
                </th>
                <td>
                  {rudi.isLoading
                    ? 'Loading...'
                    : rudi.error
                      ? 'Unavailable'
                      : credential?.source
                        ? sources[credential.source]
                        : 'No key available'}
                </td>
                <td>
                  {chat.isLoading
                    ? 'Loading...'
                    : chat.error
                      ? 'Unavailable'
                      : configured
                        ? 'Deployment key'
                        : 'No key available'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-sm text-muted-foreground">
        Existing Rudi global and tenant keys retain their precedence over deployment keys. The key
        form below changes Rudi's stored global keys; it does not write to OpenBao or change Chat's
        credentials.
      </p>
    </section>
  );
}
